/**
 * Grand Quack Auto — Photo Mode (B4).
 *
 * P key freezes the world, hides HUD, enables orbit camera.
 * Mouse drag orbits around the duck. ENTER captures + opens share modal.
 * Press P again to exit.
 */

import type { System, World } from '../types';
import { consumePhotoCapture, consumePhotoToggle, mouseDeltaX, mouseDeltaY } from './inputSystem';
import { captureAndShare } from '../ui/shareButton';

let active = false;
let orbitYaw = 0;
let orbitPitch = 0.4; // ~23° above horizon
let orbitRadius = 8;
let savedTimeScale = 1;

const PITCH_MIN = 0.1;
const PITCH_MAX = 1.4;
const SENSITIVITY = 0.004;

let overlayEl: HTMLElement | null = null;

function createOverlay(): void {
  if (overlayEl) return;
  overlayEl = document.createElement('div');
  overlayEl.id = 'photo-overlay';
  overlayEl.innerHTML = `
    <div class="photo-bar photo-bar-top"></div>
    <div class="photo-bar photo-bar-bottom"></div>
    <div class="photo-hint">
      <span class="key-badge">P</span> Exit &nbsp;&nbsp;
      <span class="key-badge">ENTER</span> Capture
    </div>
    <div class="photo-crosshair">+</div>
  `;
  document.body.appendChild(overlayEl);
}

function removeOverlay(): void {
  overlayEl?.remove();
  overlayEl = null;
}

function enter(world: World): void {
  active = true;
  savedTimeScale = world.time.timeScale;
  world.time.timeScale = 0;

  // Initialize orbit from current camera position relative to duck.
  const dx = world.camera.position.x - world.duck.position.x;
  const dy = world.camera.position.y - world.duck.position.y;
  const dz = world.camera.position.z - world.duck.position.z;
  orbitRadius = Math.sqrt(dx * dx + dy * dy + dz * dz);
  orbitYaw = Math.atan2(dx, dz);
  orbitPitch = Math.asin(Math.min(Math.max(dy / orbitRadius, -1), 1));
  orbitPitch = Math.max(PITCH_MIN, Math.min(PITCH_MAX, orbitPitch));

  const hud = document.getElementById('hud');
  if (hud) hud.style.display = 'none';
  createOverlay();
  document.body.style.cursor = 'crosshair';
}

function exit(world: World): void {
  active = false;
  world.time.timeScale = savedTimeScale;
  const hud = document.getElementById('hud');
  if (hud) hud.style.display = '';
  removeOverlay();
  document.body.style.cursor = '';
}

export function isPhotoModeActive(): boolean {
  return active;
}

export const photoModeSystem: System = {
  name: 'photoMode',

  fixedUpdate(_dt: number, world: World) {
    if (consumePhotoToggle()) {
      if (active) exit(world);
      else enter(world);
    }

    if (active && consumePhotoCapture()) {
      captureAndShare(world);
    }
  },

  render(_dt: number, _alpha: number, world: World) {
    if (!active) return;

    // Update orbit from mouse delta.
    orbitYaw -= mouseDeltaX * SENSITIVITY;
    orbitPitch -= mouseDeltaY * SENSITIVITY;
    orbitPitch = Math.max(PITCH_MIN, Math.min(PITCH_MAX, orbitPitch));

    // Compute camera position on orbit sphere.
    const cosPitch = Math.cos(orbitPitch);
    const camX = world.duck.position.x + orbitRadius * Math.sin(orbitYaw) * cosPitch;
    const camY = world.duck.position.y + orbitRadius * Math.sin(orbitPitch);
    const camZ = world.duck.position.z + orbitRadius * Math.cos(orbitYaw) * cosPitch;

    world.camera.position.set(camX, camY, camZ);
    world.camera.lookAt(
      world.duck.position.x,
      world.duck.position.y + 0.6,
      world.duck.position.z,
    );
  },
};
